import { Injectable, inject } from '@angular/core';
import { Observable, map, of, take } from 'rxjs';

import { AuxiliaryDataFetchDetails } from '@gfe/core/models/entities/entity-visualization-interface';
import {
  PlanFilter,
  PlanFilterOperator
} from '@gfe/core/models/entity/entity-selector-request.model';

import { AuxiliaryDataContext } from '../auxiliary-data/auxiliary-data.service';
import { SelectorsService } from '../selectors/selectors.service';

/**
 * Fetches the complete entities behind a set of uids, for the consumers a `SUMMARY` table row
 * cannot serve: the map and timeline, and the expanded detail panel.
 *
 * Cached per uid for the same reason `AuxiliaryDataService` is — the visualization path is
 * re-entered on every selection change, and each change usually adds a few uids to a set
 * already resolved.
 *
 * Deliberately a **separate cache** from `AuxiliaryDataService`: both would key `UserRoute`
 * uids, and a summary row cached by one would then be served as a full route to the other.
 * The two, plus `ReferenceNamesService`, now share one cache shape; extracting it is worth
 * doing as its own step.
 */
@Injectable({ providedIn: 'root' })
export class EntityHydrationService {
  private readonly selectorsService = inject(SelectorsService);

  /** cacheKey(context) -> (versionName -> (uid -> full entity)). */
  private readonly cache = new Map<string, Map<string, Map<string, unknown>>>();

  hydrate$(
    entityType: string,
    versionName: string,
    uids: string[],
    ctx: AuxiliaryDataContext
  ): Observable<Map<string, unknown>> {
    const known = this.versionCache(ctx, versionName);
    const wanted = Array.from(new Set(uids));
    const missing = wanted.filter((uid) => !known.has(uid));

    if (missing.length === 0) {
      return of(this.pick(wanted, known));
    }

    const details: AuxiliaryDataFetchDetails = {
      versionNameAndUidsPerType: { [entityType]: [versionName, missing] }
    };

    return this.selectorsService
      .fetchAuxiliaryData$(details, ctx.dataSpaceId, this.planFilter(ctx), 'FULL')
      .pipe(
        take(1),
        map((fetched) => {
          for (const entity of fetched.entitiesArrayPerVersionName[versionName] ?? []) {
            const uid = (entity as { uid?: string })?.uid;
            if (uid != null) known.set(uid, entity);
          }
          return this.pick(wanted, known);
        })
      );
  }

  /** Drops everything; call when a write may have changed what was hydrated. */
  invalidate(): void {
    this.cache.clear();
  }

  /**
   * A uid the backend did not return is left out rather than faked. Callers fall back to the
   * summary row they already hold, so a partial result degrades instead of blanking the view.
   */
  private pick(uids: string[], known: Map<string, unknown>): Map<string, unknown> {
    const result = new Map<string, unknown>();
    for (const uid of uids) {
      const entity = known.get(uid);
      if (entity !== undefined) result.set(uid, entity);
    }
    return result;
  }

  private planFilter(ctx: AuxiliaryDataContext): PlanFilter | undefined {
    if (ctx.planId == null) return undefined;
    return { id: ctx.planId, operator: ctx.planOperator ?? PlanFilterOperator.IN };
  }

  private versionCache(
    ctx: AuxiliaryDataContext,
    versionName: string
  ): Map<string, unknown> {
    const key = `${ctx.dataSpaceId}|${ctx.planId ?? ''}|${ctx.planOperator ?? ''}`;
    let byVersion = this.cache.get(key);
    if (!byVersion) {
      byVersion = new Map();
      this.cache.set(key, byVersion);
    }
    let byUid = byVersion.get(versionName);
    if (!byUid) {
      byUid = new Map();
      byVersion.set(versionName, byUid);
    }
    return byUid;
  }
}
